/* script */
//
//
//
//
//
//

// Utils
import { cloneDeep, debounce, isEmpty, isMatch } from 'lodash';
import * as utils from 'libs/utils/helpers/fabric';

const __vue_script__ = {
    name: 'DesignPreview',

    props: {
        event: {
            type: Object,
            required: true
        },

        value: {
            type: Array,
            required: true
        },

        height: {
            type: Number,
            required: true
        },

        width: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            bg: null,
            canvas: null,
            currentPage: 0,
            debouncedPaint: debounce(this.paint, 1000),
            editable: false,
            innerValue: {},
            parentClass: null,
            ready: false,
            resizeObserver: null,
            showUnfitMessage: false
        };
    },

    computed: {
        /** @returns {object} */
        currentConfig() {
            const pages = this.innerValue || [];
            return pages[this.currentPage]?.blocks || {};
        }
    },

    watch: {
        /**
         * NOTE: this watcher is causing double flashing
         * when the value is updated from the parent.
         *
         * This watcher will not be needed in the moment
         * we will be able to remove the JSON editor.
         *
         * Future: Move the logic in the mounted hook.
         */
        value: {
            deep: true,
            immediate: true,
            handler() {
                console.debug(`[${this.$options.name}] Painting from config`);
                this.innerValue = cloneDeep(this.value);
                this.debouncedPaint();
            }
        },

        async height() {
            console.debug(`[${this.$options.name}] Painting from height`);
            this.debouncedPaint();
        },

        async width() {
            console.debug(`[${this.$options.name}] Painting from width`);
            this.debouncedPaint();
        },

        showUnfitMessage(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.$nextTick(() => this.setCanvasSize());
            }
        },

        ready() {
            if (this.ready && this.config?.length) {
                this.debouncedPaint();
            }
        }
    },

    async mounted() {
        this.parentClass = `.${Array.from(this.$refs.wrapper.classList.values()).join('.')}`;
        await this.prepareCanvas();
    },

    async beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (typeof this.canvas?.dispose === 'function') {
            await this.canvas.dispose();
        }
    },

    methods: {
        emit() {
            console.warn('[DesignPreview] "emit" not implemented in preview');
        },

        updateAllAndEmit() {
            console.warn('[DesignPreview] "updateAllAndEmit" not implemented in preview');
        },

        getSize() {
            const canvas = this.$refs.canvas;
            const parent = canvas.closest(this.parentClass);
            const rect = parent.getBoundingClientRect();

            const width = rect.width - 2; // NOTE: 2px for the left and right borders in the CSS;
            const height = rect.height - 2; // NOTE: 2px for the top and bottom borders in the CSS

            return { width, height };
        },

        async prepareCanvas() {
            const backgroundImage = await this.loadBackground();
            this.bg = backgroundImage;

            const canvasOptions = {
                ...this.getSize(),
                backgroundImage
            };

            let callbacks;
            if (this.editable) {
                const self = this;
                callbacks = {
                    cleared(objects) { return self.updateAllAndEmit(objects); },
                    modified(objects) { return self.updateAllAndEmit(objects); }
                };
            }

            this.canvas = utils.newCanvas(this.$refs.canvas, canvasOptions, callbacks);

            /**
             * The resize observer is used to dected EXTERNAL interference with the
             * size of the whole component. It won't be triggered from internal
             * height changes. Meaning if something happens within `.design-preview`
             * that affects the changes of the children, this won't be triggered
             * unless the changes will also propagate to the parent.
             */
            this.resizeObserver = new ResizeObserver(() => this.setCanvasSize());
            this.resizeObserver.observe(this.$refs.wrapper);

            this.ready = true;
        },

        setCanvasSize() {
            if (!this.$refs.canvas) return;

            const messages = this.$refs.messages;
            let { height, width } = this.getSize();

            if (messages) {
                height -= this.$utils.dom.fullHeight(messages);
            }

            this.canvas.setHeight(height);
            this.canvas.setWidth(width);
        },

        async paint() {
            if (!this.canvas) {
                return;
            }
            return new Promise((resolve) => {
                requestAnimationFrame(() => {
                    this.draw().then(() => resolve());
                    this.$nextTick(() => utils.zoomToFit(this.canvas));
                });
            });
        },

        async draw() {
            console.info('[DesignPreview] Repainting...');
            this.canvas.clear();

            const bounds = utils.canvasBoundaries({ width: this.width, height: this.height });
            this.canvas.add(bounds);
            this.canvas.backgroundImage = this.bg;

            for (const qr of this.currentConfig.qrcode || []) {
                const angle = qr.angle || 0;
                qr.angle = 0; // Reset angle to rotate the element according to its center
                qr.selectable = this.editable;
                const img = await utils.newSvgImage(this.$utils.assets.imagePath('qr-code.svg'), qr, updates => {
                    Object.assign(qr, updates);

                    if (this.editable) {
                        this.emit();
                    }
                });
                img.rotate(angle);
                this.canvas.add(img);
            }

            for (const config of this.currentConfig.images || []) {
                config.selectable = this.editable;
                await this.addImage(config);
            }

            for (const config of this.currentConfig.text || []) {
                config.selectable = this.editable;
                await this.addTextBox(config);
            }

            await this.canvas.renderAll();
        },

        async addTextBox(text) {
            const { fontFaceId, angle = 0 } = text;
            text.angle = 0; // Reset angle to rotate the element according to its center
            if (fontFaceId) {
                try {
                    const { fp_attachments_s3 } = await this.$services.documents.getDocById(this.event._id, fontFaceId, true);
                    const fileName = Object.keys(fp_attachments_s3 || {}).at(0);

                    if (fileName) {
                        const fontUrl = this.$utils.assets.getEventAttachmentPath(this.event._id, fileName, fontFaceId);
                        await this.$utils.dom.loadFontFace(fontFaceId, fontUrl);
                        text.fontFamily = fontFaceId;
                    }
                } catch (error) {
                    console.error('[DesignPreview] Could not load custom font', error);
                }
            }

            const txt = await utils.newTextBox(text.text, text, updates => {
                if (!isMatch(text, updates)) {
                    Object.assign(text, updates);

                    if (this.editable) {
                        this.emit();
                    }
                }
            });
            txt.rotate(angle);
            this.canvas.add(txt);
        },

        async addImage(config) {
            try {
                const eid = this.event._id;
                const { id: imageId, angle = 0 } = config;
                config.angle = 0; // Reset angle to rotate the element according to its center

                const { fp_attachments_s3 } = await this.$services.documents.getDocById(eid, imageId);
                const fileName = Object.keys(fp_attachments_s3 || {}).at(0);

                if (isEmpty(fileName)) {
                    return;
                }

                const url = this.$utils.assets.getEventAttachmentPath(eid, fileName, imageId);

                const img = await utils.loadImage(url, config, updates => {
                    Object.assign(config, updates);

                    if (this.editable) {
                        this.emit();
                    }
                });
                img.rotate(angle);
                this.canvas.add(img);
            } catch (error) {
                console.error('[DesignPreview] Could not load image', error);
            }
        },

        async loadBackground() {
            try {
                if (!this.currentConfig.background?.id) {
                    return;
                }

                const eid = this.event._id;
                const bgId = this.currentConfig.background.id;
                const { fp_attachments_s3 } = await this.$services.documents.getDocById(eid, bgId);
                const fileName = Object.keys(fp_attachments_s3 || {}).at(0);

                if (isEmpty(fileName)) {
                    return;
                }

                const url = this.$utils.assets.getEventAttachmentPath(eid, fileName, bgId);
                const bg = await utils.buildBackground(url);
                const width = bg.getScaledWidth();
                const height = bg.getScaledHeight();
                const unfit = width !== this.width || height !== this.height;

                this.showUnfitMessage = unfit;

                return bg;
            } catch (error) {
                console.error('[DesignPreview] Could not load background', error);
            }

        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"wrapper",staticClass:"design-preview"},[(_vm.showUnfitMessage)?_c('div',{ref:"messages",staticClass:"alert alert-info"},[_vm._v(_vm._s(_vm.$t('onsite_scanning.design.messages.bg_not_fitting', [_vm.width, _vm.height])))]):_vm._e(),_c('div',{ref:"preview",staticClass:"design"},[_c('canvas',{ref:"canvas"})])])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "DesignPreview.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__